
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { priceDropNotifications } from '../../mocks/priceAlerts';
import type { PriceDropNotification } from '../../mocks/priceAlerts';

type FilterStatus = 'all' | 'triggered' | 'active' | 'expired';

const NotificationsPage = () => {
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [notifications, setNotifications] = useState(priceDropNotifications);

  const filteredNotifications = notifications.filter(notification => {
    if (filterStatus === 'all') return true;
    return notification.status === filterStatus;
  });

  const stats = {
    total: notifications.length,
    triggered: notifications.filter(n => n.status === 'triggered').length,
    active: notifications.filter(n => n.status === 'active').length,
    expired: notifications.filter(n => n.status === 'expired').length,
    totalSaved: notifications
      .filter(n => n.status === 'triggered')
      .reduce((sum, n) => sum + (n.originalPrice - n.droppedPrice), 0),
  };

  const handleDelete = (id: number) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const getStatusBadge = (status: PriceDropNotification['status']) => {
    switch (status) {
      case 'triggered':
        return (
          <span className="flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full">
            <i className="ri-check-line"></i>
            Price Dropped
          </span>
        );
      case 'active':
        return (
          <span className="flex items-center gap-1 text-xs font-medium text-teal-700 bg-teal-100 px-2 py-1 rounded-full">
            <i className="ri-time-line"></i>
            Watching
          </span>
        );
      case 'expired':
        return (
          <span className="flex items-center gap-1 text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
            <i className="ri-close-line"></i>
            Expired
          </span>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-teal-50 to-emerald-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <Link to="/" className="hover:text-gray-700 cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-900">Price Alerts</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Price Drop Notifications</h1>
          <p className="text-gray-600">Track your price alerts and see how much you have saved</p>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                <i className="ri-notification-3-line text-teal-600 text-xl"></i>
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-sm text-gray-500">Total Alerts</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <i className="ri-check-double-line text-green-600 text-xl"></i>
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.triggered}</p>
            <p className="text-sm text-gray-500">Triggered</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <i className="ri-eye-line text-amber-600 text-xl"></i>
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
            <p className="text-sm text-gray-500">Active</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <i className="ri-money-dollar-circle-line text-emerald-600 text-xl"></i>
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">${stats.totalSaved}</p>
            <p className="text-sm text-gray-500">Total Saved</p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="bg-white rounded-xl border border-gray-200 mb-6">
          <div className="flex border-b">
            {[
              { key: 'all', label: 'All', count: stats.total },
              { key: 'triggered', label: 'Triggered', count: stats.triggered },
              { key: 'active', label: 'Active', count: stats.active },
              { key: 'expired', label: 'Expired', count: stats.expired },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setFilterStatus(tab.key as FilterStatus)}
                className={`flex-1 py-4 px-4 text-sm font-medium transition-colors cursor-pointer relative ${
                  filterStatus === tab.key
                    ? 'text-teal-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label} ({tab.count})
                {filterStatus === tab.key && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600" />
                )}
              </button>
            ))}
          </div>

          {/* Notifications List */}
          <div className="divide-y divide-gray-100">
            {filteredNotifications.length === 0 ? (
              <div className="py-16 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-notification-off-line text-3xl text-gray-400"></i>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No notifications</h3>
                <p className="text-gray-500 mb-6">
                  {filterStatus === 'all'
                    ? 'Start tracking products to receive price drop alerts'
                    : `No ${filterStatus} alerts found`}
                </p>
                <Link
                  to="/shop"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-search-line"></i>
                  Browse Products
                </Link>
              </div>
            ) : (
              filteredNotifications.map(notification => (
                <div
                  key={notification.id}
                  className={`p-5 hover:bg-gray-50 transition-colors ${
                    notification.status === 'triggered' ? 'bg-green-50/50' : ''
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <Link to={`/product/${notification.productId}`} className="cursor-pointer">
                      <img
                        src={notification.productImage}
                        alt={notification.productName}
                        className="w-20 h-20 rounded-lg object-contain bg-white border border-gray-100"
                      />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            {getStatusBadge(notification.status)}
                            <span className="text-xs text-gray-400">
                              {notification.triggeredAt
                                ? getTimeAgo(notification.triggeredAt)
                                : `Created ${formatDate(notification.createdAt)}`}
                            </span>
                          </div>
                          <Link
                            to={`/product/${notification.productId}`}
                            className="font-medium text-gray-900 hover:text-teal-600 transition-colors cursor-pointer"
                          >
                            {notification.productName}
                          </Link>
                          <p className="text-sm text-gray-500 mt-0.5">at {notification.retailer}</p>
                        </div>
                        <button
                          onClick={() => handleDelete(notification.id)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                          title="Delete alert"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>

                      <div className="mt-3 flex items-center gap-6">
                        <div>
                          <p className="text-xs text-gray-400 mb-0.5">Original Price</p>
                          <p className="text-sm font-medium text-gray-500 line-through">
                            ${notification.originalPrice}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-0.5">Target Price</p>
                          <p className="text-sm font-medium text-gray-900">
                            ${notification.targetPrice}
                          </p>
                        </div>
                        {notification.status === 'triggered' && (
                          <>
                            <div className="w-8 flex items-center justify-center">
                              <i className="ri-arrow-right-line text-gray-300"></i>
                            </div>
                            <div>
                              <p className="text-xs text-green-600 mb-0.5">Dropped To</p>
                              <p className="text-lg font-bold text-green-600">
                                ${notification.droppedPrice}
                              </p>
                            </div>
                            <div className="ml-auto">
                              <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                                <i className="ri-arrow-down-line"></i>
                                {notification.dropPercentage}% off
                              </span>
                            </div>
                          </>
                        )}
                        {notification.status === 'active' && (
                          <div className="ml-auto text-sm text-gray-500">
                            <i className="ri-time-line mr-1"></i>
                            Expires {formatDate(notification.expiresAt)}
                          </div>
                        )}
                      </div>

                      {notification.status === 'triggered' && (
                        <div className="mt-4">
                          <Link
                            to={`/product/${notification.productId}`}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            View Deal
                            <i className="ri-external-link-line"></i>
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Tips Section */}
        <div className="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-xl p-6 text-white">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-lightbulb-line text-2xl"></i>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Pro Tips for Better Deals</h3>
              <ul className="text-sm text-teal-100 space-y-1">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line"></i>
                  Set alerts 15-20% below current price for realistic targets
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line"></i>
                  Track products before major sales events like Black Friday
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line"></i>
                  Compare prices across multiple retailers for best deals
                </li>
              </ul>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NotificationsPage;
